import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
import { DynamicTableStateless } from '@atlaskit/dynamic-table';
import EmptyState from '@atlaskit/empty-state';
import { token } from '@atlaskit/tokens';
import { type IntlShape, injectIntl } from '@atlassian/jira-intl/src/index.tsx';
import getTableRows from './body/index.tsx';
import getTableHeader from './header/index.tsx';
import messages from './messages.tsx';
import type { IssueTableData } from './types.tsx';

type DIProps = {
	intl: IntlShape;
};

type Props = IssueTableData &
	DIProps & {
		onPageChange: (targetPage: number) => void;
	};

const IssueTable = ({
	page,
	pageSize,
	loading,
	intl,
	onPageChange,
	rows = [],
	columns = [],
	totalIssues,
}: Props) => {
	const paginationi18n = useMemo(
		() => ({
			prev: intl.formatMessage(messages.paginationPrev),
			next: intl.formatMessage(messages.paginationNext),
		}),
		[intl],
	);

	const head = useMemo(() => getTableHeader(columns), [columns]);

	const formattedRows = useMemo(
		() => getTableRows({ rows, totalIssues, page, pageSize }),
		[pageSize, page, rows, totalIssues],
	);

	const emptyView = <EmptyState header={intl.formatMessage(messages.noData)} />;

	return (
		<TableContainer data-test-id="servicedesk-reports-common.ui.table.issue-table">
			<DynamicTableStateless
				testId="servicedesk-reports-common.ui.table.issue-table.issue-table"
				page={page}
				rowsPerPage={pageSize}
				head={head}
				rows={formattedRows}
				onSetPage={onPageChange}
				isLoading={loading}
				// @ts-expect-error - TS2741 - Property 'label' is missing in type '{ prev: string; next: string; }' but required in type 'I18nShape'.
				paginationi18n={paginationi18n}
				emptyView={emptyView}
				isFixedSize
			/>
		</TableContainer>
	);
};

export default injectIntl(IssueTable);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableContainer = styled.div({
	margin: `${token('space.100', '8px')} 0`,
});
